import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Terminal as TerminalIcon } from 'lucide-react';
import { MONACO_LAYOUT_FLUSH_EVENT } from '../ui/ResizableSplitter';
import { isTauri } from '../../utils/tauri';
import { subscribeTauriEvent } from '../../utils/safeTauriEvent';
import { useFtpStore } from '../../store/ftpStore';
import { useTerminalExtStore, tabKind } from '../../store/terminalExtStore';
import SshConnectionModal from './SshConnectionModal';
import TerminalNewMenu from './TerminalNewMenu';
import TerminalAllTabsMenu from './TerminalAllTabsMenu';
import TerminalTabItem from './TerminalTabItem';
import TerminalTabToolbar from './TerminalTabToolbar';
import TerminalPaneGrid from './TerminalPaneGrid';
import TerminalEmpty from './TerminalEmpty';
import TerminalLayoutsMenu from './TerminalLayoutsMenu';
import SaveLayoutModal from './SaveLayoutModal';
import AgentCwdModal from './AgentCwdModal';
import { newTabCwd } from './terminalDefaultCwd';
import { setActiveTab as showPanel } from '../../store/uiModalStore';
import './TerminalExt.css';
import './TerminalLight.css';
import { useTerminalIsDark } from '../../hooks/useTerminalIsDark';

/**
 * Terminal extension content — full width, no sidebar. The tab bar mirrors
 * the Collections tab bar (All-tabs dropdown left, etm-tab strip, and a "+"
 * on the right of the tabs) and is always visible; "+" opens a menu with the
 * local shells and "SSH connection…", which opens a modal picker over the
 * vault's saved SSH connections. Each tab is a grid of split panes
 * (TerminalPaneGrid); grids stay mounted while their tab is open (hidden,
 * not unmounted — the shells keep running). The active tab's layout actions
 * sit right-aligned in the bar (TerminalTabToolbar).
 */

export default function TerminalExtView({ active = true }) {
  const { t } = useTranslation();
  const termIsDark = useTerminalIsDark();
  const tabs = useTerminalExtStore((s) => s.tabs);
  const activeTabId = useTerminalExtStore((s) => s.activeTabId);
  const setActiveTab = useTerminalExtStore((s) => s.setActiveTab);
  const closeTab = useTerminalExtStore((s) => s.closeTab);
  const openLocalTab = useTerminalExtStore((s) => s.openLocalTab);
  const openSshTab = useTerminalExtStore((s) => s.openSshTab);
  const activeTab = tabs.find((tab) => tab.id === activeTabId) || null;

  const [sshPickerOpen, setSshPickerOpen] = useState(false);
  const [saveLayoutFor, setSaveLayoutFor] = useState(null);

  // The panel stays mounted while other panels are shown (App.js hides it
  // with display:none so the shells keep running) — on re-activation, ask
  // every terminal to refit, since the window may have resized meanwhile.
  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(
      () => window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT)),
      60
    );
    return () => clearTimeout(timer);
  }, [active]);

  // Vault edits (new/renamed connections) keep the picker list fresh.
  useEffect(() => {
    if (!isTauri()) return undefined;
    const sub = subscribeTauriEvent('vault:changed', () => {
      useFtpStore.getState().loadConnections();
    });
    return () => sub.unlisten();
  }, []);

  const openLocal = (shell) => {
    const base = shell?.label || 'Terminal';
    const count = tabs.filter((tab) => tab.label.startsWith(base)).length;
    openLocalTab(count === 0 ? base : `${base} ${count + 1}`, shell?.key, newTabCwd());
  };

  // Titlebar "+" (ExtensionTitleBarActions 'terminal.panel' header).
  useEffect(() => {
    const onNew = () => {
      const st = useTerminalExtStore.getState();
      const count = st.tabs.filter((tab) => tabKind(st, tab) === 'local').length;
      openLocalTab(count === 0 ? 'Terminal' : `Terminal ${count + 1}`, undefined, newTabCwd());
    };
    window.addEventListener('terminal-new-local', onNew);
    return () => window.removeEventListener('terminal-new-local', onNew);
  }, [openLocalTab]);

  if (!isTauri()) {
    return (
      <div className="term-ext-view">
        <div className="term-ext-empty">
          <TerminalIcon size={22} />
          {t('terminalExt.desktopOnly', 'Terminal is available in the desktop app')}
        </div>
      </div>
    );
  }

  return (
    // `term-light`: the terminal surface and pane chrome are light
    // (TerminalLight.css); the tab bar always follows the app theme.
    <div className={termIsDark ? 'term-ext-view' : 'term-ext-view term-light'}>
      {/* Collections tab-bar structure: the outer bar never scrolls (so the
          dropdowns can escape it); tabs scroll inside .etm-tabs-strip. */}
      {/* minHeight pins the bar to its with-tabs height (tab 26px + strip
          padding 6px + border 1px) so it doesn't shrink when only the
          All-tabs and + buttons remain. */}
      <div
        className="flex items-center border-b border-gray-200 bg-gray-50 app-tabs-border flex-shrink-0"
        style={{ height: 36 }} // = TAB_STRIP_HEIGHT of the shared strip (ui/EnhancedTabManager)
      >
        {/* Visible tabs — scroll inside the strip, like Collections. */}
        <div className="flex min-w-0 etm-tabs-strip">
          {tabs.map((tab) => (
            <TerminalTabItem
              key={tab.id}
              tab={tab}
              active={tab.id === activeTabId}
              onSelect={setActiveTab}
              onClose={closeTab}
              onSaveLayout={setSaveLayoutFor}
            />
          ))}
        </div>

        {/* "+" — new terminal / SSH connection picker. */}
        <TerminalNewMenu onOpenLocal={openLocal} onOpenSsh={() => setSshPickerOpen(true)} />

        <TerminalAllTabsMenu tabs={tabs} activeTabId={activeTabId} onSelect={setActiveTab} onClose={closeTab} />

        {/* A tab opened from a project page ("Open in terminal"): the way back. */}
        {activeTab?.returnTo?.panel && (
          <button
            type="button"
            className="term-return-chip"
            data-testid="terminal-return"
            onClick={() => showPanel(activeTab.returnTo.panel)}
          >
            <ArrowLeft size={12} aria-hidden />
            {activeTab.returnTo.label
              ? t('terminalExt.backToProjectNamed', { name: activeTab.returnTo.label })
              : t('terminalExt.backToProject')}
          </button>
        )}

        {activeTab && <TerminalTabToolbar tab={activeTab} layoutsMenu={<TerminalLayoutsMenu tab={activeTab} />} />}
      </div>

      <div className="term-ext-body">
        {tabs.length === 0 ? (
          <TerminalEmpty onOpenShell={() => openLocal(null)} onOpenSsh={() => setSshPickerOpen(true)} />
        ) : (
          tabs.map((tab) => <TerminalPaneGrid key={tab.id} tab={tab} visible={tab.id === activeTabId} />)
        )}
      </div>

      {saveLayoutFor && <SaveLayoutModal tabId={saveLayoutFor} onClose={() => setSaveLayoutFor(null)} />}

      <AgentCwdModal />

      <SshConnectionModal
        isOpen={sshPickerOpen}
        onClose={() => setSshPickerOpen(false)}
        onPick={(conn) => {
          openSshTab(conn.id, conn.name);
          setSshPickerOpen(false);
        }}
      />
    </div>
  );
}
